import { noteLiked, noteReplySubmitted } from "@/gtmEvents/notes";
import useAdminStatus from "@/hooks/useAdminStatus";
import useCurrentUser from "@/hooks/useCurrentUser";
import pushToDataLayer from "@/lib/pushToDataLayer";
import LikeButton from "@/src/notes/components/LikeButton";
import NoteImages from "@/src/notes/components/NoteImages";
import NoteMenu from "@/src/notes/components/NoteMenu";
import ReplyButton from "@/src/notes/components/ReplyButton";
import ReportButton from "@/src/notes/components/ReportButton";
import type { NoteType } from "@/src/notes/types/NoteType";
import { CheckIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Pin } from "lucide-react";
import { observer } from "mobx-react";
import { Fragment, useState } from "react";
import Avatar from "./Avatar";
import Form from "./Form";

dayjs.extend(relativeTime);

type NoteAction = "editing" | "replying" | null;

const Note = ({
  note,
  depth = 0,
  chefId,
  canReply = true,
}: {
  note: NoteType;
  depth?: number;
  chefId?: number;
  canReply?: boolean;
}) => {
  const { currentUser, isLoggedIn } = useCurrentUser();

  const [isReported, setIsReported] = useState(false);

  const { isUserAdmin } = useAdminStatus();

  const [action, setAction] = useState<NoteAction>(null);

  const showMobToken =
    note.user.role === "elevated" && note.user.chefId !== chefId;

  const showChefToken = note.user.chefId === chefId;

  const handleReport = () => {
    setIsReported(true);
  };

  const handleEditComplete = () => {
    setAction(null);
  };

  const handleReplyComplete = (reply: NoteType) => {
    setAction(null);

    pushToDataLayer(noteReplySubmitted, {
      recipeId: reply.element?.id,
      recipeTitle: reply.element?.title,
      recipeSlug: reply.element?.slug,
      noteUid: reply.uid,
    });
  };

  const handleLike = (likedNote: NoteType) => {
    pushToDataLayer(noteLiked, {
      recipeId: likedNote.element?.id,
      recipeTitle: likedNote.element?.title,
      recipeSlug: likedNote.element?.slug,
      noteUid: likedNote.uid,
    });
  };

  const showMenu =
    isLoggedIn && (currentUser?.uid === note.user?.uid || isUserAdmin);

  return (
    <Fragment>
      <div
        className={clsx("font-body text-sm", {
          "pointer-events-none opacity-50": isReported,
        })}
      >
        <div className={clsx("flex flex-col")}>
          <div className={clsx("mb-4 flex w-full items-center space-x-2")}>
            <Avatar user={note.user} />
            <div className={clsx("")}>{note.user.displayName}</div>

            <div className={clsx("font-normal text-zinc-500")}>
              {dayjs(note.createdAt).fromNow()}
            </div>

            {note.isEdited && (
              <div className={clsx("font-normal text-zinc-500")}>(edited)</div>
            )}

            {(showChefToken || showMobToken) && (
              <span className={clsx("text-base")}>·</span>
            )}

            {showChefToken && (
              <div
                className={clsx(
                  "flex items-center space-x-1 font-normal text-zinc-500"
                )}
              >
                <div className={clsx("text-sky-500 opacity-75")}>
                  <CheckIcon className={clsx("size-3.5 stroke-[2]")} />
                </div>
                <div>Chef</div>
              </div>
            )}

            {showMobToken && (
              <div
                className={clsx(
                  "flex items-center space-x-1 font-normal text-zinc-500"
                )}
              >
                <div className={clsx("text-green-500 opacity-75")}>
                  <CheckIcon className={clsx("size-3.5 stroke-[2]")} />
                </div>
                <div>Admin</div>
              </div>
            )}

            {note.isPinned && (
              <div
                className={clsx(
                  "!ml-auto flex items-center space-x-1 font-normal text-zinc-500"
                )}
              >
                <div className={clsx("")}>
                  <Pin className={clsx("size-4 stroke-[1.5]")} />
                </div>
              </div>
            )}
          </div>

          {action !== "editing" && (
            <div>
              <div
                className={clsx("whitespace-pre-line font-normal")}
                dangerouslySetInnerHTML={{ __html: note.content }}
              ></div>
              <NoteImages imageUrls={note.imageUrls} />
            </div>
          )}

          {action === "editing" && (
            <div>
              <Form
                note={note}
                elementId={note.elementId}
                onEditComplete={handleEditComplete}
              />
            </div>
          )}

          {action === "replying" && depth === 0 && (
            <div className={clsx("py-4 pb-3")}>
              <Form
                elementId={note.elementId}
                parentUid={note.uid}
                onCreateComplete={handleReplyComplete}
              />
            </div>
          )}

          <div
            className={clsx("flex items-end", {
              "pt-4": !showMenu,
            })}
          >
            <div className={clsx("flex items-center space-x-4")}>
              <div>
                <LikeButton
                  note={note}
                  disabled={!isLoggedIn || action === "editing"}
                  onLike={handleLike}
                />
              </div>
              {depth === 0 && (
                <div>
                  <ReplyButton
                    disabled={!isLoggedIn || !canReply || action === "editing"}
                    isActive={action === "replying"}
                    onClick={() =>
                      setAction(action === "replying" ? null : "replying")
                    }
                  />
                </div>
              )}
              {currentUser?.uid !== note.user?.uid && (
                <div>
                  <ReportButton
                    disabled={isReported}
                    note={note}
                    onReport={handleReport}
                  />
                </div>
              )}
            </div>

            {showMenu && (
              <div className={clsx("ml-auto")}>
                <NoteMenu
                  note={note}
                  onEditRequest={() => setAction("editing")}
                />
              </div>
            )}
          </div>
        </div>
        {note.replies && note.replies.length > 0 && depth === 0 && (
          <div className={clsx("space-y-6 pt-6")}>
            {note.replies.map((reply) => (
              <div
                key={reply.uid}
                className={clsx("border-l-2 border-zinc-950 pl-6")}
              >
                <Note note={reply} chefId={chefId} depth={1} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default observer(Note);
